import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Sparkles, Copy, Download, ChevronDown, ChevronUp } from 'lucide-react'
import { api } from '../../api/client'
import Button from '../ui/Button'
import Input, { Select, Textarea } from '../ui/Input'
import type { LucideIcon } from 'lucide-react'

export interface ToolField {
  name: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'number' | 'checkboxes'
  required?: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
  defaultValue?: string | string[]
  showWhen?: (values: Record<string, any>) => boolean
}

interface ToolPageProps {
  title: string
  icon: LucideIcon
  description: string
  endpoint: string | ((values: Record<string, any>) => string)
  fields: ToolField[]
  renderResult: (result: any) => React.ReactNode
  transformPayload?: (values: Record<string, any>) => Record<string, any>
}

export default function ToolPage({ title, icon: Icon, description, endpoint, fields, renderResult, transformPayload }: ToolPageProps) {
  const [values, setValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {}
    fields.forEach(f => { init[f.name] = f.defaultValue ?? (f.type === 'checkboxes' ? [] : '') })
    return init
  })
  const [showRaw, setShowRaw] = useState(false)

  const mutation = useMutation({
    mutationFn: (payload: Record<string, any>) => {
      const ep = typeof endpoint === 'function' ? endpoint(values) : endpoint
      return api.post<any>(ep, payload)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload = transformPayload ? transformPayload(values) : { ...values }
    Object.keys(payload).forEach(k => {
      if (payload[k] === '' || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k]
    })
    mutation.mutate(payload)
  }

  const updateValue = (name: string, value: any) => setValues(v => ({ ...v, [name]: value }))

  const copyResult = () => {
    if (mutation.data) navigator.clipboard.writeText(JSON.stringify(mutation.data, null, 2))
  }

  const downloadResult = () => {
    if (!mutation.data) return
    const blob = new Blob([JSON.stringify(mutation.data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title.toLowerCase().replace(/\s+/g, '_')}_result.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Icon size={20} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(field => {
              if (field.showWhen && !field.showWhen(values)) return null

              if (field.type === 'select') {
                return (
                  <Select
                    key={field.name}
                    label={field.label}
                    options={field.options || []}
                    value={values[field.name] || ''}
                    onChange={(e) => updateValue(field.name, e.target.value)}
                  />
                )
              }
              if (field.type === 'textarea') {
                return (
                  <Textarea
                    key={field.name}
                    label={field.label}
                    value={values[field.name] || ''}
                    onChange={(e) => updateValue(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )
              }
              if (field.type === 'checkboxes') {
                return (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                    <div className="flex flex-wrap gap-2">
                      {(field.options || []).map(opt => {
                        const checked = (values[field.name] || []).includes(opt.value)
                        return (
                          <label
                            key={opt.value}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                              checked
                                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = values[field.name] || []
                                updateValue(
                                  field.name,
                                  e.target.checked
                                    ? [...current, opt.value]
                                    : current.filter((v: string) => v !== opt.value),
                                )
                              }}
                              className="sr-only"
                            />
                            {opt.label}
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )
              }
              return (
                <Input
                  key={field.name}
                  label={field.label}
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={values[field.name] || ''}
                  onChange={(e) => updateValue(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )
            })}
            <Button type="submit" loading={mutation.isPending} className="w-full">
              <Sparkles size={16} /> Generate
            </Button>
            {mutation.error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                {(mutation.error as Error).message}
              </div>
            )}
          </form>
        </div>

        {/* Result */}
        <div>
          {mutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm">Generating... This may take 30-60 seconds.</p>
            </div>
          )}
          {mutation.data && !mutation.isPending && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={copyResult}>
                  <Copy size={14} /> Copy JSON
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadResult}>
                  <Download size={14} /> Download
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowRaw(!showRaw)}>
                  {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showRaw ? 'Hide' : 'Show'} Raw
                </Button>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {renderResult(mutation.data)}
              </div>

              {showRaw && (
                <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-96">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(mutation.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          {!mutation.data && !mutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <Sparkles size={32} className="mb-3 opacity-30" />
              <p className="text-sm">Fill the form and click Generate</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
